import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import {
  DialogBody,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import type { Document } from '@/modules/documents/domain/entities/document';
import { DeleteDocument } from '@/modules/documents/view/mutations/delete-document';
import { UpdateDocument } from '@/modules/documents/view/mutations/update-document';
import { toastResult } from '@/utils/toast';

export type DocumentDeleteDialogProps = {
  document: Document;
  isOpen: boolean;
  siteId?: string;
  onClose: () => void;
  scope: 'site' | 'document';
};

export function DocumentDeleteDialog({
  document,
  isOpen,
  siteId,
  onClose,
  scope,
}: DocumentDeleteDialogProps) {
  const deleteDocument = DeleteDocument.useMutation();
  const updateDocument = UpdateDocument.useMutation();

  async function handleOnDelete() {
    if (!siteId) {
      throw new Error('siteId is required for document deletion');
    }

    if (scope === 'document') {
      toastResult(deleteDocument.mutateAsync({ siteId, document }), {
        success: `Document "${document.name}" deleted successfully`,
        error: `Failed to delete "${document.name}"`,
        exception: 'Failed to delete',
      });
    } else {
      toastResult(
        updateDocument.mutateAsync({
          siteIds: [siteId],
          document: {
            id: document.id,
            siteIds: document.sites
              .filter((site) => site.siteId !== siteId)
              .map((site) => site.siteId),
          },
        }),
        {
          success: `Document "${document.name}" removed successfully`,
          error: `Failed to remove "${document.name}"`,
          exception: 'Failed to remove',
        },
      );
    }
  }

  return (
    <ConfirmDialog
      open={isOpen}
      onSubmit={handleOnDelete}
      onClose={onClose}
      submitText={scope === 'document' ? 'Delete' : 'Remove'}
    >
      <DialogTitle>
        {scope === 'document' ? <>Delete document?</> : <>Remove document?</>}
      </DialogTitle>
      <DialogDescription>
        <DocumentDescription
          siteId={siteId ?? ''}
          document={document}
          scope={scope}
        />
      </DialogDescription>
      <DialogBody>
        <div className="m-6 border-t border-gray-100">
          <dl className="divide-y divide-gray-100">
            <DetailRow label="Name" value={document.name} />
            <DetailRow label="Type" value={document.type} />
            <DetailRow label="Tags" value={document.tags.join(', ')} />
            <DetailRow
              label="Uploaded by"
              value={document.uploadedBy.displayName}
            />
          </dl>
        </div>
      </DialogBody>
    </ConfirmDialog>
  );
}

type DetailRowProps = {
  label: string;
  value: string;
};

function DetailRow({ label, value }: DetailRowProps) {
  return (
    <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
      <dt className="text-sm font-medium leading-6 text-gray-900">{label}</dt>
      <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
        {value}
      </dd>
    </div>
  );
}

type DocumentDescriptionProps = {
  document: Document;
  scope: 'document' | 'site';
  siteId: string;
};

function DocumentDescription({
  document,
  scope,
  siteId,
}: DocumentDescriptionProps) {
  if (scope === 'document') {
    if (document.sites.length > 1) {
      return (
        <div className="flex flex-col gap-2">
          <p>
            Are you sure you want to delete this document? The is action will
            affect all of its associated sites, listed below:
            <ul className="my-2 list-disc pl-6">
              {document.sites.map((s) => (
                <li key={s.siteId}>{s.name}</li>
              ))}
            </ul>
          </p>
          <p>This action cannot be undone.</p>
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-2">
        <p>Are you sure you want to delete this document?</p>
        <p> This action cannot be undone.</p>
      </div>
    );
  }

  const siteName = document.sites.find((site) => site.siteId === siteId)?.name;
  if (siteName) {
    return (
      <div className="flex flex-col gap-2">
        <p>
          Are you sure you want to remove this document from{' '}
          <span className="font-semibold">{siteName}</span>?
        </p>
        <p>
          The document will still remain for the following sites:
          <ul className="my-2 list-disc pl-6">
            {document.sites
              .filter((site) => site.siteId !== siteId)
              .map((s) => (
                <li key={s.siteId}>{s.name}</li>
              ))}
          </ul>
        </p>
      </div>
    );
  }

  return (
    <>Are you sure you want to remove this document from the current site?</>
  );
}
